import type { IStreamVideo } from '@/shared/types/common';

/**
 * Байты не пришли полностью: загрузка идёт в другой вкладке, прервана или брошена
 * (`streamState` из деталей урока; бэкенд: docs/CLOUDFLARE_STREAM_SETUP.md, «Сверка при чтении»)
 */
export const isVideoUploadInterrupted = (video: IStreamVideo) =>
  video.status === 'pending' &&
  (video.streamState === 'pendingupload' || video.streamState === 'missing');

/** Stream обрабатывает принятое видео (или состояние ещё неизвестно) */
export const isVideoProcessing = (video: IStreamVideo) =>
  video.status === 'pending' && !isVideoUploadInterrupted(video);

/**
 * Прерванную загрузку можно продолжить тем же файлом: байты ещё в Stream (`pendingupload`, срок
 * приёма не вышел), адрес известен. При `missing` байтов уже нет, грузить только заново
 */
export const isVideoUploadResumable = (video: IStreamVideo) =>
  video.status === 'pending' &&
  video.streamState === 'pendingupload' &&
  !!video.resumableUpload &&
  (!video.uploadExpiresAt || Date.parse(video.uploadExpiresAt) > Date.now());

/**
 * Адрес, по которому файл докачивается с места обрыва: тот же файл (имя, размер, дата
 * изменения), что был выбран для незавершённой загрузки шага. Другой файл — новая загрузка
 */
export const getResumableUploadUrl = (
  video: IStreamVideo | null | undefined,
  file: File,
): string | null => {
  const resume = video?.resumableUpload;
  if (!video || !resume || !isVideoUploadResumable(video)) return null;
  const isSameFile =
    file.name === video.name &&
    file.size === resume.fileSize &&
    file.lastModified === resume.fileLastModified;
  return isSameFile ? resume.url : null;
};
